import React, { useState } from 'react';
import { useDarkMode } from '../../../context/DarkModeContext';
import { SearchIcon, FilterIcon, InstagramIcon, FacebookIcon, TwitterIcon, DownloadIcon, HeartIcon, Share2Icon, LayoutIcon } from 'lucide-react';
import BackButton from '../../../components/UI/BackButton';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
// Mock data for templates
const templates = [{
  id: 1,
  title: 'Minimalist Book Launch',
  creator: 'LitLaunch Free',
  price: 0,
  platforms: ['instagram', 'facebook'],
  type: 'Book Announcement',
  image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  downloads: 1245
}, {
  id: 2,
  title: 'Dark Romance Quote',
  creator: 'Gothic Designs',
  price: 5,
  platforms: ['instagram', 'pinterest'],
  type: 'Review Quote',
  image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  downloads: 856
}, {
  id: 3,
  title: 'Fantasy Map Reveal',
  creator: 'Cartography Co.',
  price: 12,
  platforms: ['twitter', 'facebook'],
  type: 'Cover Reveal',
  image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  downloads: 432
}, {
  id: 4,
  title: 'Author Q&A Session',
  creator: 'LitLaunch Free',
  price: 0,
  platforms: ['instagram', 'tiktok'],
  type: 'Author Q&A',
  image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  downloads: 2100
}, {
  id: 5,
  title: 'Flash Sale Alert',
  creator: 'Promo Masters',
  price: 8,
  platforms: ['instagram', 'facebook', 'twitter'],
  type: 'Sale/Promo',
  image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  downloads: 654
}, {
  id: 6,
  title: 'Character Aesthetic',
  creator: 'Visual Storyteller',
  price: 15,
  platforms: ['pinterest', 'instagram'],
  type: 'Book Announcement',
  image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  downloads: 321
}];
const SocialTemplates = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('All');
  const [selectedPrice, setSelectedPrice] = useState('All');
  // Filter logic
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlatform = selectedPlatform === 'All' || template.platforms.includes(selectedPlatform.toLowerCase());
    const matchesPrice = selectedPrice === 'All' ? true : selectedPrice === 'Free' ? template.price === 0 : template.price > 0;
    return matchesSearch && matchesPlatform && matchesPrice;
  });
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return <InstagramIcon className="h-4 w-4" data-id="element-19878" data-mp="2cr546w4d3alh" />;
      case 'facebook':
        return <FacebookIcon className="h-4 w-4" data-id="element-19879" data-mp="3acattqgj3wau" />;
      case 'twitter':
        return <TwitterIcon className="h-4 w-4" data-id="element-19880" data-mp="35ej4n479qoac" />;
      default:
        return <Share2Icon className="h-4 w-4" data-id="element-19881" data-mp="35gccgmtmbejx" />;
    }
  };
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-19882" data-mp="30mhimqkatj74">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-19883" data-mp="30l6gev6i4ji4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8" data-id="element-19884" data-mp="2vnwuhpibmpz1">
          <div data-id="element-19885" data-mp="3t5epfhdb4j83">
            <div className="mb-4" data-id="element-19886" data-mp="3t5wvf5prubt7">
              <BackButton data-id="element-19887" data-mp="3oddfi23m34l8" />
            </div>
            <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-19888" data-mp="3obkccws7j2hz">
              Social Media Templates
            </h1>
            <p className={`max-w-2xl ${dk.textSecondary}`} data-id="element-19889" data-mp="38xgl9pgjo06i">
              Professional, ready-to-use templates designed specifically for
              authors and book promotion.
            </p>
          </div>
          <button className={`mt-4 md:mt-0 px-4 py-2 rounded-md transition-colors flex items-center text-white ${rt.primaryButton}`} data-id="element-19890" data-mp="33w10lyia3von">
            <LayoutIcon className="h-4 w-4 mr-2" data-id="element-19891" data-mp="3rm3pk5qwclj1" />
            Request Custom Design
          </button>
        </div>

        {/* Filters */}
        <div className={`p-4 rounded-xl shadow-sm mb-8 border ${dk.card}`} data-id="element-19892" data-mp="3hvs6td2k07s3">
          <div className="flex flex-col md:flex-row gap-4" data-id="element-19893" data-mp="3htplh6ccj2tp">
            <div className="flex-1 relative" data-id="element-19894" data-mp="3mttrg2fmof5o">
              <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textFaint}`} data-id="element-19895" data-mp="3msimhzag51cz" />
              <input type="text" placeholder="Search templates..." className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-19896" data-mp="j61u7tgvtuo5" />
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 md:pb-0" data-id="element-19897" data-mp="28mxdzmljk6et">
              <select className={`px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} value={selectedPlatform} onChange={e => setSelectedPlatform(e.target.value)} data-id="element-19898" data-mp="28np96wb8824k">
                <option value="All" data-id="element-19899" data-mp="1tz2muan6ma3u">All Platforms</option>
                <option value="Instagram" data-id="element-19900" data-mp="1pwfqpxjha30u">Instagram</option>
                <option value="Facebook" data-id="element-19901" data-mp="1puuw67x5yk2z">Facebook</option>
                <option value="Twitter" data-id="element-19902" data-mp="35cn2sb78pu05">Twitter/X</option>
                <option value="TikTok" data-id="element-19903" data-mp="35f9bie5vafjy">TikTok</option>
                <option value="Pinterest" data-id="element-19904" data-mp="31ajgssmy370m">Pinterest</option>
              </select>
              <select className={`px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} value={selectedPrice} onChange={e => setSelectedPrice(e.target.value)} data-id="element-19905" data-mp="1y170gk7cyq39">
                <option value="All" data-id="element-19906" data-mp="27x0l6qv8dpw4">All Prices</option>
                <option value="Free" data-id="element-19907" data-mp="2bz5bpgqdu6ej">Free</option>
                <option value="Paid" data-id="element-19908" data-mp="1fa62nzhz50la">Paid</option>
              </select>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-19909" data-mp="1kabyzsz6vb9p">
          {filteredTemplates.map(template => <div key={template.id} className={`rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border ${dk.card}`} data-id="element-19910" data-mp="1o9ksqxu8b959">
              <div className="relative aspect-video" data-id="element-19911" data-mp="wiwsdo0dq7sn">
                <img src={template.image} alt={template.title} className="w-full h-full object-cover" data-id="element-19912" data-mp="10l1i4bnpika0" />
                <div className="absolute top-2 right-2" data-id="element-19913" data-mp="szo4u0haxmko">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${template.price === 0 ? 'bg-green-100 text-green-800' : rt.badge}`} data-id="element-19914" data-mp="o3qrm31c4wc4">
                    {template.price === 0 ? 'Free' : `$${template.price}`}
                  </span>
                </div>
              </div>
              <div className="p-5" data-id="element-19915" data-mp="a47ip80p9gyr">
                <div className="flex justify-between items-start mb-2" data-id="element-19916" data-mp="5cg29qb9crmc">
                  <div data-id="element-19917" data-mp="5a3cws367ez9">
                    <h3 className={`font-bold text-lg ${dk.textPrimary}`} data-id="element-19918" data-mp="ctptj397fel">
                      {template.title}
                    </h3>
                    <p className={`text-sm ${dk.textMuted}`} data-id="element-19919" data-mp="3si70c290jujm">
                      by {template.creator}
                    </p>
                  </div>
                  <button className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} data-id="element-19920" data-mp="3iipvfn1l5kop">
                    <HeartIcon className="h-5 w-5 text-gray-400 hover:text-red-500 transition-colors" data-id="element-19921" data-mp="1aajezo7t2lrw" />
                  </button>
                </div>
                <div className="flex items-center gap-2 mb-4" data-id="element-19922" data-mp="119lkbwj9bqlz">
                  {template.platforms.map(platform => <div key={platform} className={`p-1.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`} title={platform} data-id="element-19923" data-mp="167n30gcrtnwx">
                      {getPlatformIcon(platform)}
                    </div>)}
                  <span className={`text-xs ml-auto ${dk.textFaint}`} data-id="element-19924" data-mp="igr4z1hq1wh8">
                    {template.downloads} uses
                  </span>
                </div>
                <button className={`w-full py-2 rounded-md transition-colors flex items-center justify-center font-medium text-white ${rt.primaryButton}`} data-id="element-19925" data-mp="8lphsb70b4ha">
                  {template.price === 0 ? <>
                      <DownloadIcon className="h-4 w-4 mr-2" data-id="element-19926" data-mp="3l4icl4it32k3" />
                      Use Template
                    </> : <>Purchase Template</>}
                </button>
              </div>
            </div>)}
        </div>

        {filteredTemplates.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-19927" data-mp="cvign9m3zpg4">
            <FilterIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textFaint}`} data-id="element-19928" data-mp="7xgp00amaht1" />
            <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-19929" data-mp="mlbihiwg8q76">
              No templates found
            </h3>
            <p className={dk.textMuted} data-id="element-19930" data-mp="hpmpxziuk9kd">
              Try adjusting your filters or search term.
            </p>
          </div>}
      </div>
    </div>;
};
export default SocialTemplates;